import { useCallback, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";

export interface AdminPropertyRow {
  id: string;
  title: string;
  slug: string | null;
  general_location?: string | null;
  public_location_label?: string | null;
  publication_status: string;
  show_on_website: boolean;
  booking_enabled: boolean;
  hero_image_url?: string | null;
}

const ALL = "all";

export function useAdminProperty() {
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const selected = params.get("property") || ALL;

  const { data: properties = [] } = useQuery<AdminPropertyRow[]>({
    queryKey: ["/api/admin/properties"],
    staleTime: 15_000,
  });

  const active = useMemo(
    () => properties.find((p) => p.slug === selected || p.id === selected) || null,
    [properties, selected],
  );

  const setProperty = useCallback((slugOrAll: string) => {
    const next = new URLSearchParams(params);
    if (!slugOrAll || slugOrAll === ALL) next.delete("property");
    else next.set("property", slugOrAll);
    const qs = next.toString();
    const path = location.split("?")[0];
    setLocation(qs ? `${path}?${qs}` : path);
  }, [location, params, setLocation]);

  return {
    properties,
    selectedSlug: selected,
    isAllProperties: selected === ALL,
    active,
    setProperty,
  };
}
